/**
 * ProximityValueInput — value editor for the segment `proximity` field.
 *
 * value shape = [anchor, radiusMiles]:
 *   within_radius_of_zip      → anchor = 5-digit ZIP (text input)
 *   within_radius_of_org      → anchor = organization id (debounced org search)
 *   within_radius_of_location → anchor = location id (debounced location search)
 *   within_radius_of_event    → anchor = event id (debounced event search); the event's geocoded
 *                               coordinates (migration 009) are the center. If the event isn't
 *                               geocoded, the criterion is not-evaluable server-side, so we surface
 *                               an inline warning after selection.
 *
 * Emits [anchor, miles] on every change. The DB compiler (_segment_geo_predicate) is the source of
 * truth for validity — an empty/unresolvable anchor or out-of-range radius fails closed there — so
 * this input only enforces light client-side hints (5-digit ZIP, 1–500 miles).
 */
import { useEffect, useRef, useState } from "react";
import type {
  FieldOperator,
  QueryConditionValue,
} from "../../types/list.types";
import {
  searchOrganizations,
  getOrganization,
} from "../../services/organizations.service";
import {
  searchLocationsGlobal,
  getLocation,
} from "../../services/locations.service";
import { getEventById } from "../../services/events.service";
import EventPicker from "../events/EventPicker";
import { preventEnterSubmit } from "../../utils/preventEnterSubmit";

type ProximityOperator =
  | "within_radius_of_zip"
  | "within_radius_of_org"
  | "within_radius_of_location"
  | "within_radius_of_event";

// An event contributes coordinates to the proximity center only if it's geocoded.
function eventIsGeocoded(e: {
  latitude?: number | null;
  longitude?: number | null;
}): boolean {
  return (
    e.latitude !== null &&
    e.latitude !== undefined &&
    e.longitude !== null &&
    e.longitude !== undefined
  );
}

interface ProximityValueInputProps {
  operator: FieldOperator;
  value: QueryConditionValue;
  onChange: (value: [string, number]) => void;
}

const DEFAULT_MILES = 20;

function parseValue(value: QueryConditionValue): {
  anchor: string;
  miles: number;
} {
  if (Array.isArray(value) && value.length === 2) {
    const anchor = String(value[0] ?? "");
    const milesNum = Number(value[1]);
    return {
      anchor,
      miles:
        Number.isFinite(milesNum) && milesNum > 0 ? milesNum : DEFAULT_MILES,
    };
  }
  return { anchor: "", miles: DEFAULT_MILES };
}

interface EntityOption {
  id: string;
  label: string;
}

/**
 * Debounced typeahead that stores an entity id but displays its name. `search` returns options;
 * `resolveLabel` fetches the label for a preset id (edit rehydration).
 */
function EntitySearch({
  anchorId,
  placeholder,
  search,
  resolveLabel,
  onPick,
}: {
  anchorId: string;
  placeholder: string;
  search: (q: string) => Promise<EntityOption[]>;
  resolveLabel: (id: string) => Promise<string | null>;
  onPick: (id: string) => void;
}) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<EntityOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  // Rehydrate the display label when editing a saved criterion (anchorId set, label unknown).
  useEffect(() => {
    let cancelled = false;
    if (anchorId && selectedLabel === null) {
      resolveLabel(anchorId)
        .then((label) => {
          if (!cancelled && label) {
            setSelectedLabel(label);
          }
        })
        .catch(() => {
          /* leave label unresolved; id still applies */
        });
    }
    if (!anchorId) {
      setSelectedLabel(null);
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorId]);

  // Debounced search.
  useEffect(() => {
    if (!term.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const opts = await search(term);
        setResults(opts);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [term, search]);

  // Close the dropdown on outside click.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={boxRef} className="relative flex-1 min-w-[200px]">
      {anchorId && selectedLabel !== null ? (
        <div className="flex items-center gap-2 px-2 py-1 border border-gray-300 rounded text-sm bg-gray-50">
          <span className="truncate">{selectedLabel}</span>
          <button
            type="button"
            onClick={() => {
              setSelectedLabel(null);
              setTerm("");
              onPick("");
            }}
            className="ml-auto text-gray-400 hover:text-gray-700"
            title="Change"
          >
            ✕
          </button>
        </div>
      ) : (
        <input
          onKeyDown={preventEnterSubmit}
          type="text"
          value={term}
          placeholder={placeholder}
          onChange={(e) => setTerm(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
        />
      )}
      {open && (results.length > 0 || loading) && (
        <ul className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-200 rounded shadow-lg text-sm">
          {loading && <li className="px-2 py-1 text-gray-400">Searching…</li>}
          {results.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => {
                  onPick(r.id);
                  setSelectedLabel(r.label);
                  setOpen(false);
                  setTerm("");
                }}
                className="w-full text-left px-2 py-1 hover:bg-gray-100"
              >
                {r.label}
              </button>
            </li>
          ))}
          {!loading && results.length === 0 && (
            <li className="px-2 py-1 text-gray-400">No matches</li>
          )}
        </ul>
      )}
    </div>
  );
}

export function ProximityValueInput({
  operator,
  value,
  onChange,
}: ProximityValueInputProps) {
  const { anchor, miles } = parseValue(value);
  const op = operator as ProximityOperator;

  const setAnchor = (a: string) => onChange([a, miles]);
  const setMiles = (m: number) =>
    onChange([anchor, Number.isFinite(m) && m > 0 ? m : DEFAULT_MILES]);

  // For an event anchor, warn if the selected event has no coordinates — proximity
  // is not-evaluable server-side (CRITERIA_NOT_EVALUABLE) and the segment can't count.
  const [eventGeocoded, setEventGeocoded] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (op !== "within_radius_of_event" || !anchor) {
      setEventGeocoded(null);
      return;
    }
    getEventById(anchor)
      .then((e) => {
        if (!cancelled) {
          setEventGeocoded(e ? eventIsGeocoded(e) : null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEventGeocoded(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [op, anchor]);

  return (
    <div className="flex flex-wrap items-center gap-2 flex-1">
      {op === "within_radius_of_zip" && (
        <input
          type="text"
          inputMode="numeric"
          maxLength={5}
          value={anchor}
          placeholder="ZIP (5 digits)"
          onChange={(e) =>
            setAnchor(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))
          }
          className="w-32 px-2 py-1 border border-gray-300 rounded text-sm"
        />
      )}

      {op === "within_radius_of_org" && (
        <EntitySearch
          anchorId={anchor}
          placeholder="Search organizations…"
          search={async (q) =>
            (await searchOrganizations(q)).map((o) => ({
              id: o.id,
              label: o.name,
            }))
          }
          resolveLabel={async (id) => {
            try {
              return (await getOrganization(id))?.name ?? null;
            } catch {
              return null;
            }
          }}
          onPick={setAnchor}
        />
      )}

      {op === "within_radius_of_location" && (
        <EntitySearch
          anchorId={anchor}
          placeholder="Search locations…"
          search={async (q) =>
            (await searchLocationsGlobal(q)).map((l) => ({
              id: l.id,
              label: [l.name, l.city, l.state].filter(Boolean).join(" · "),
            }))
          }
          resolveLabel={async (id) => {
            try {
              const l = await getLocation(id);
              return l
                ? [l.name, l.city, l.state].filter(Boolean).join(" · ")
                : null;
            } catch {
              return null;
            }
          }}
          onPick={setAnchor}
        />
      )}

      {op === "within_radius_of_event" && (
        // The shared EventPicker (paged groups + date / id search) instead of a
        // 15-row name search: every event is reachable, including one of many
        // recurring events with the same name, venue and city.
        <div className="flex-1 min-w-[280px]">
          <EventPicker
            value={anchor}
            onChange={(id) => setAnchor(id)}
            testId="proximity-event-picker"
            required
          />
        </div>
      )}

      <div className="flex items-center gap-1">
        <span className="text-xs text-gray-500">within</span>
        <input
          type="number"
          min={1}
          max={500}
          value={Number.isFinite(miles) ? miles : ""}
          onChange={(e) => setMiles(Number(e.target.value))}
          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
        />
        <span className="text-xs text-gray-500">miles</span>
      </div>

      {op === "within_radius_of_event" && anchor && eventGeocoded === false && (
        <p className="basis-full text-xs text-amber-600">
          This event isn't geocoded (no venue coordinates), so this criterion
          won't match any contacts. Add a ZIP or city/state to the event's
          location, or pick a geocoded event.
        </p>
      )}
    </div>
  );
}
